import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useAcademy } from '../../hooks/useAcademy';
import { PLYRMATCH_AGENCY_ID } from '../../utils/portalDomain';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import { useLeadBotWaitingCount } from '../../hooks/useLeadBot';
import {
  HomeIcon,
  Cog6ToothIcon,
  UsersIcon,
  BuildingOffice2Icon,
  PlusCircleIcon,
  UserGroupIcon,
  DocumentIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChatBubbleLeftRightIcon,
  MapIcon,
  CheckCircleIcon,
  BoltIcon,
  SparklesIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CalendarIcon,
  FolderOpenIcon,
  TicketIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';

const adminNav = [
  { to: '/admin/dashboard',       labelKey: 'common:routeTitles.dashboard',       icon: HomeIcon,                 permission: 'dashboard' },
  // No `permission` key — visible to every staffRole/coach regardless of
  // their PERMISSION_KEYS. Which subjects they can actually ask about is
  // gated separately, conversationally, by AGENT_TOPIC_KEYS/agentTopics
  // (see src/utils/permissions.js) — enforced server-side in
  // functions/assistant.js, not by hiding this nav item.
  { to: '/admin/assistant',       labelKey: 'common:routeTitles.assistant',       icon: SparklesIcon },
  { to: '/admin/calendar',        labelKey: 'common:routeTitles.calendar',        icon: CalendarDaysIcon,         permission: 'calendar',       alsoMatch: ['/admin/visits', '/admin/travel-forms'] },
  { to: '/admin/players',         labelKey: 'common:routeTitles.players',         icon: UserGroupIcon,            permission: 'players' },
  { to: '/admin/contracts',       labelKey: 'common:routeTitles.contracts',       icon: DocumentCheckIcon,        permission: 'contracts',      alsoMatch: ['/admin/contract-templates'] },
  { to: '/admin/quotes',          labelKey: 'common:sidebar.quotesAndInvoices',   icon: BanknotesIcon,          permission: ['quotes', 'invoices'], alsoMatch: ['/admin/invoices'] },
  { to: '/admin/messages',        labelKey: 'common:routeTitles.messages',        icon: ChatBubbleLeftRightIcon,  permission: 'messages' },
  { to: '/admin/crm/communications', labelKey: 'common:routeTitles.communications', icon: ChatBubbleLeftRightIcon, permission: 'communications' },
  { to: '/admin/automation',      labelKey: 'common:routeTitles.automation',      icon: BoltIcon,                 permission: 'emailTemplates' },
  { to: '/admin/settings',        labelKey: 'common:sidebar.settings',            icon: Cog6ToothIcon,            permission: 'settings' },
];

const crmNav = [
  { to: '/admin/crm/dashboard',      labelKey: 'common:routeTitles.dashboard',      icon: HomeIcon },
  // Same cross-section item as adminNav above — not CRM- or Onboarding-
  // specific, so it appears in both nav lists rather than only one.
  { to: '/admin/assistant',          labelKey: 'common:routeTitles.assistant',      icon: SparklesIcon },
  { to: '/admin/crm/calendar',       labelKey: 'common:routeTitles.calendar',       icon: CalendarDaysIcon },
  { to: '/admin/crm/leads',          labelKey: 'common:routeTitles.leads',          icon: UserGroupIcon,           alsoMatch: ['/admin/crm/leads/new'] },
  // Website lead bot add-on — only shown when a super admin has switched it
  // on for the academy (see the leadBotEnabled filter below).
  { to: '/admin/crm/lead-bot',       labelKey: 'common:routeTitles.leadBot',        icon: ChatBubbleOvalLeftEllipsisIcon, leadBotOnly: true },
  // Booked calls, booking links, call availability and staff-scheduled
  // video calls — the old Video Calls page is a tab of it now.
  { to: '/admin/crm/bookings',       labelKey: 'common:routeTitles.bookings',       icon: CalendarIcon,            alsoMatch: ['/admin/crm/video-calls'] },
  { to: '/admin/crm/quotes',         labelKey: 'common:routeTitles.quotes',         icon: DocumentTextIcon },
  { to: '/admin/contracts',          labelKey: 'common:routeTitles.contracts',      icon: DocumentCheckIcon,       alsoMatch: ['/admin/contracts/new', '/admin/contract-templates'] },
  { to: '/admin/crm/tasks',          labelKey: 'common:routeTitles.tasks',          icon: CheckCircleIcon },
  { to: '/admin/crm/communications', labelKey: 'common:routeTitles.communications', icon: ChatBubbleLeftRightIcon },
  { to: '/admin/automation',         labelKey: 'common:routeTitles.automation',     icon: BoltIcon },
  { to: '/admin/settings',           labelKey: 'common:sidebar.settings',           icon: Cog6ToothIcon },
];

// Pages reachable from both modes' nav — visiting one shouldn't flip which
// nav set is showing; whichever mode the user was last actively in stays
// shown underneath it. Contracts is a single permissions-driven view shared
// by CRM and Onboarding (same URL either way — see Contracts.jsx).
const SHARED_PATHS = ['/admin/settings', '/admin/contracts', '/admin/contract-templates', '/admin/automation', '/admin/crm/communications', '/admin/invoices', '/admin/assistant', '/admin/help'];

// Super Admin stays English-only, so these are plain labels rather than
// translation keys (Sidebar.jsx is shared by every role, including Super
// Admin, but this array specifically is not in scope for translation).
const superNav = [
  { to: '/super/tenants',        label: 'Academies',   icon: BuildingOffice2Icon },
  { to: '/super/create-academy', label: 'New Academy', icon: PlusCircleIcon },
  { to: '/super/leads',          label: 'Academy Leads', icon: UserGroupIcon, alsoMatch: ['/super/leads/new'] },
  { to: '/super/finance',        label: 'Finance',     icon: BanknotesIcon },
  { to: '/super/contract-templates', label: 'Contract Templates', icon: DocumentCheckIcon, alsoMatch: ['/super/contract-templates/new'] },
  { to: '/super/chats',          label: 'Support Chats', icon: ChatBubbleLeftRightIcon },
  { to: '/super/resources',      label: 'Resources',    icon: FolderOpenIcon },
  { to: '/super/trial-codes',    label: 'Free Trial Codes', icon: TicketIcon },
  { to: '/super/help',           label: 'Help Centre',  icon: QuestionMarkCircleIcon },
  { to: '/super/staff',          label: 'Sales Staff',  icon: UsersIcon },
  { to: '/super/settings',       label: 'Settings',    icon: Cog6ToothIcon },
];

// IAP's own platform-level sales staff (not an academy's own salesRep) —
// shares the same two routes a superAdmin uses to add tenants, plus the full
// Academy Sales CRM (leads/finance/contracts — see requireSuperAdminOrIapSales
// in functions/index.js), the support chat inbox and Resources (brochures), but no Settings and no
// Sales Staff item of their own (only a real superAdmin can invite/manage
// other sales staff — see SalesStaff.jsx).
const iapSalesNav = [
  { to: '/super/tenants',        label: 'Academies',   icon: BuildingOffice2Icon },
  { to: '/super/create-academy', label: 'New Academy', icon: PlusCircleIcon },
  { to: '/super/leads',          label: 'Academy Leads', icon: UserGroupIcon, alsoMatch: ['/super/leads/new'] },
  { to: '/super/finance',        label: 'Finance',     icon: BanknotesIcon },
  { to: '/super/contract-templates', label: 'Contract Templates', icon: DocumentCheckIcon, alsoMatch: ['/super/contract-templates/new'] },
  { to: '/super/chats',          label: 'Support Chats', icon: ChatBubbleLeftRightIcon },
  { to: '/super/resources',      label: 'Resources',    icon: FolderOpenIcon },
  { to: '/super/trial-codes',    label: 'Free Trial Codes', icon: TicketIcon },
];

const guardianNav = [
  { to: '/portal/dashboard',  labelKey: 'common:routeTitles.dashboard',    icon: HomeIcon },
  { to: '/portal/profile',    labelKey: 'common:routeTitles.profile',      icon: UsersIcon },
  { to: '/portal/documents',  labelKey: 'common:routeTitles.documents',    icon: DocumentIcon },
  { to: '/portal/finances',   labelKey: 'common:routeTitles.finances',     icon: BanknotesIcon },
  { to: '/portal/contracts',  labelKey: 'common:routeTitles.contracts',    icon: DocumentCheckIcon },
  { to: '/portal/travel',     labelKey: 'common:routeTitles.travelForms',  icon: MapIcon },
  { to: '/portal/messages',   labelKey: 'common:routeTitles.messages',     icon: ChatBubbleLeftRightIcon },
  { to: '/portal/calendar',   labelKey: 'common:routeTitles.calendar',     icon: CalendarDaysIcon },
];

function NavItem({ to, label, labelKey, icon: Icon, collapsed, alsoMatch, badge }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const extraActive = alsoMatch?.some((p) => pathname.startsWith(p)) ?? false;
  const showBadge = badge > 0;
  const resolvedLabel = labelKey ? t(labelKey) : label;
  return (
    <NavLink
      to={to}
      title={collapsed ? resolvedLabel : undefined}
      className={({ isActive }) => `sidebar-nav-item ${(isActive || extraActive) ? 'active' : ''}`}
    >
      {collapsed ? (
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Icon style={{ width: 18, height: 18, display: 'block', marginLeft: 'var(--icon-ml, 0)' }} className="sidebar-icon" />
          {showBadge && (
            <div style={{
              position: 'absolute', top: -3, right: -4,
              minWidth: 14, height: 14, borderRadius: 7, padding: '0 3px',
              background: '#ef4444', border: '1.5px solid var(--sidebar-bg)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 8, fontWeight: 800, color: '#fff', lineHeight: 1,
            }}>
              {badge > 9 ? '9+' : badge}
            </div>
          )}
        </div>
      ) : (
        <>
          <Icon style={{ width: 18, height: 18, flexShrink: 0, marginLeft: 'var(--icon-ml, 0)' }} className="sidebar-icon" />
          <span style={{ flex: 1 }}>{resolvedLabel}</span>
          {showBadge && (
            <span style={{
              minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
              background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function getInitials(displayName, email) {
  if (displayName) {
    return displayName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  }
  return (email?.[0] ?? '?').toUpperCase();
}

export default function Sidebar({ mobileOpen, onMobileClose }) {
  const { t } = useTranslation();
  const { user, isSuperAdmin, isIapSales, isAcademyAdmin, isGuardian, isSalesRep, isCoach, isAgencyTenant, canManageAcademyPortal, userProfile, can, access, leadBotEnabled, academyId } = useAuth();
  const leadBotWaiting = useLeadBotWaitingCount(leadBotEnabled && (isAcademyAdmin || isSalesRep));
  const { academy, loading: academyLoading } = useAcademy();
  const [collapsed, setCollapsed] = useState(false);
  const unreadCount = useUnreadMessages();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Close mobile drawer on navigation
  React.useEffect(() => {
    onMobileClose?.();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // CRM (pre-sales) vs Onboarding (post-sales) — tracked as its own state
  // rather than derived purely from the current path, so a shared page like
  // Settings doesn't silently kick the user out of whichever mode they were
  // actually in. Persisted so a refresh on a shared page keeps it too.
  // Keyed per-account — unscoped, the last account tested in this browser
  // (staff or guardian; guardians use this same key for their Player/Academy
  // Portal choice, see router.jsx and ChoosePortal.jsx) would leak its mode
  // into the next account's first load here.
  const portalModeKey = `portalMode:${user?.uid}`;
  // Falls back to 'onboarding', not 'crm', when this account has no CRM
  // access at all (e.g. a partner academy staff member — see
  // AuthContext's portalAccess) — otherwise a first-ever login with no
  // stored preference would briefly try to render the CRM nav for someone
  // who can't see it.
  const [mode, setMode] = useState(() => {
    const fallback = access.includes('crm') ? 'crm' : 'onboarding';
    try { return localStorage.getItem(portalModeKey) || fallback; } catch { return fallback; }
  });

  React.useEffect(() => {
    if (SHARED_PATHS.some((p) => pathname.startsWith(p))) return;
    const next = pathname.startsWith('/admin/crm') ? 'crm' : 'onboarding';
    setMode(next);
    try { localStorage.setItem(portalModeKey, next); } catch { /* ignore */ }
  }, [pathname, portalModeKey]);

  const isCrmMode = mode === 'crm';

  // An item's `permission` is either a single key, or an array meaning
  // "show if the user has any of these" — used for the merged Quotes &
  // Invoices shortcut, since a role can have one without the other. That
  // same item's `to` is fixed at /admin/quotes, but a user granted only
  // Invoices (no default role is set up that way, but per-user overrides
  // can be) should still land somewhere they can actually see.
  const resolveNavItem = (item) => {
    if (item.to === '/admin/quotes' && !can('quotes') && can('invoices')) {
      // Relabelled too, not just re-pointed — "Quotes & Invoices" would be
      // a stale, confusing label for someone who only ever sees Invoices
      // (every agency tenant, per AuthContext's can('quotes') override; or
      // any staff member granted Invoices without Quotes).
      return { ...item, to: '/admin/invoices', alsoMatch: ['/admin/quotes'], labelKey: 'common:routeTitles.invoices' };
    }
    return item;
  };
  const canSeeNavItem = (item) => {
    if (!item.permission) return true;
    return Array.isArray(item.permission) ? item.permission.some(can) : can(item.permission);
  };

  // Only an agency tenant (e.g. PLYRMATCH) gets this — the academies/clubs/
  // colleges it places players with. Spliced in here (after Leads) rather
  // than living in crmNav directly, since no ordinary academy's CRM needs
  // it. See AuthContext's isAgencyTenant.
  const partnersNavItem = { to: '/admin/crm/partners', labelKey: 'common:routeTitles.partners', icon: BuildingOffice2Icon };
  // Same Invoices page as the merged Quotes & Invoices shortcut resolves to
  // for an agency tenant in Onboarding mode (see resolveNavItem) — spliced
  // directly into the CRM nav too, since that's where PLYRMATCH actually
  // raises these (against a lead, from LeadDetail's fee box), not
  // Onboarding. See createAgencyFeeInvoice in functions/index.js.
  const invoicesNavItem = { to: '/admin/invoices', labelKey: 'common:routeTitles.invoices', icon: BanknotesIcon, alsoMatch: ['/admin/invoices/new'] };
  const findCrmNav = (to) => crmNav.find((item) => item.to === to);
  // Quotes is dropped entirely for an agency tenant (see AuthContext's
  // can('quotes') override) — they collect a flat fee via Invoices, not the
  // estimate/proposal flow Quotes is for.
  const crmNavForTenant = isAgencyTenant
    ? [
        findCrmNav('/admin/crm/dashboard'),
        findCrmNav('/admin/assistant'),
        findCrmNav('/admin/crm/calendar'),
        findCrmNav('/admin/crm/leads'),
        partnersNavItem,
        findCrmNav('/admin/contracts'),
        invoicesNavItem,
        findCrmNav('/admin/crm/tasks'),
        findCrmNav('/admin/crm/bookings'),
        findCrmNav('/admin/crm/communications'),
        findCrmNav('/admin/automation'),
        findCrmNav('/admin/settings'),
      ]
    : crmNav;

  let navItems;
  if (isSuperAdmin) {
    navItems = superNav;
  } else if (isIapSales) {
    navItems = iapSalesNav;
  } else if (isAcademyAdmin || isSalesRep) {
    navItems = isCrmMode
      ? crmNavForTenant
      : adminNav.filter(canSeeNavItem).map(resolveNavItem);
    // Ask the Assistant isn't available to Sales Consultants yet (askAssistant
    // only allows academyAdmin/coach/superAdmin — see functions/assistant.js)
    // — hide the nav item rather than show a link that always 403s.
    if (isSalesRep) navItems = navItems.filter((item) => item.to !== '/admin/assistant');
    if (!leadBotEnabled) navItems = navItems.filter((item) => !item.leadBotOnly);
    if (isAcademyAdmin && academyId === PLYRMATCH_AGENCY_ID && can('mentorship')) {
      const settingsIndex = navItems.findIndex((item) => item.to === '/admin/settings');
      const at = settingsIndex === -1 ? navItems.length : settingsIndex;
      navItems = [...navItems.slice(0, at), { to: '/admin/mentorship', labelKey: 'common:routeTitles.mentorship', icon: AcademicCapIcon }, ...navItems.slice(at)];
    }
  } else if (isCoach) {
    // Coaches never have CRM — always the Onboarding nav, filtered down to
    // their fixed permission set (Calendar + Players — see
    // DEFAULT_PERMISSIONS.coach), same filtering academyAdmin/salesRep get.
    navItems = adminNav.filter(canSeeNavItem).map(resolveNavItem);
  } else {
    navItems = guardianNav;
  }
  // Help Centre — in both CRM and Onboarding, last in the list. The Topbar's
  // Help button opens the same thing as a side panel.
  if (isAcademyAdmin || isSalesRep || isCoach) {
    navItems = [...navItems, { to: '/admin/help', labelKey: 'common:routeTitles.helpSupport', icon: QuestionMarkCircleIcon }];
  }

  // Only offer the mode switcher to staff who actually have both sections
  // unlocked — academyAdmins always do; sales reps only if an admin granted it.
  const hasBothAccess = (isAcademyAdmin || isSalesRep) && access.length > 1;

  // "Player Portal" rather than "Guardian Portal" — this account may belong
  // to the player themselves, not necessarily a parent/guardian. Super
  // Admin's (and iapSales') own value here is never actually rendered (see
  // isAcademyScoped below) — both stay English-only, so left as plain strings.
  const sectionLabel = isSuperAdmin ? 'Super Admin'
    : isIapSales ? 'IAP Sales Staff'
    : isSalesRep ? t('common:sidebar.sectionLabels.salesConsultant', 'Sales Consultant')
    : isAcademyAdmin ? t('common:sidebar.sectionLabels.academyAdmin', 'Academy Admin')
    : isCoach ? t('common:sidebar.sectionLabels.coach', 'Coach')
    : t('common:sidebar.sectionLabels.playerPortal', 'Player Portal');
  const initials = getInitials(userProfile?.displayName, userProfile?.email);

  return (
    <div
      style={{
        width: collapsed ? 64 : 232,
        background: 'var(--sidebar-bg)',
        transition: 'width 0.22s cubic-bezier(.4,0,.2,1), transform 0.22s cubic-bezier(.4,0,.2,1)',
        flexShrink: 0,
      }}
      className={`app-sidebar flex flex-col h-screen overflow-hidden fixed inset-y-0 left-0 z-50 md:relative md:z-auto md:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} ${collapsed ? 'sidebar-collapsed' : ''}`}
    >
      {/* ── Header ── */}
      <div
        className="flex items-center shrink-0"
        style={{
          padding: '12px 14px',
          minHeight: 64,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        {(() => {
          // Fully white-labeled for anyone scoped to one academy (admins,
          // sales reps, guardians) — they see that academy's own logo once
          // set in Settings. Super admin (and iapSales, also platform-level
          // with no academyId of their own) manages every academy, so they
          // always see the platform's own IAP logo.
          const isAcademyScoped = !isSuperAdmin && !isIapSales;
          // While the academy doc is still loading, we don't yet know if it
          // has a custom logo — showing the platform's own logo here would
          // just flash and get replaced a moment later, so show nothing.
          const logoPending = isAcademyScoped && academyLoading;
          const logoSrc = isAcademyScoped && academy?.branding?.logoUrl
            ? academy.branding.logoUrl
            : `${process.env.PUBLIC_URL}/logo.png`;
          const logoAlt = isAcademyScoped && academy?.name ? academy.name : t('auth:login.logoAlt', 'International Academy Portal');

          if (collapsed) {
            return (
              <div style={{
                width: 38, height: 38, borderRadius: 7, background: '#ffffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {!logoPending && <img src={logoSrc} alt={logoAlt} style={{ height: 28, width: 'auto', maxWidth: 34, objectFit: 'contain' }} />}
              </div>
            );
          }

          if (isAcademyScoped) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, width: '100%' }}>
                <div style={{
                  borderRadius: 7, padding: '5px 8px', background: '#ffffff',
                  display: 'flex', alignItems: 'center', flexShrink: 0,
                  minWidth: logoPending ? 38 : undefined, minHeight: logoPending ? 36 : undefined,
                }}>
                  {!logoPending && <img src={logoSrc} alt={logoAlt} style={{ height: 26, width: 'auto', maxWidth: 80, objectFit: 'contain' }} />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ color: '#ffffff', fontSize: 11.5, fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {academy?.name || t('common:sidebar.academyFallback', 'Academy')}
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10.5, lineHeight: 1.2, marginTop: 1 }}>
                    {isGuardian ? t('common:sidebar.portal', 'Portal') : sectionLabel}
                  </p>
                </div>
              </div>
            );
          }

          return (
            <div style={{
              borderRadius: 8, padding: '6px 10px', background: '#ffffff',
              display: 'inline-flex', alignItems: 'center',
            }}>
              <img src={logoSrc} alt={logoAlt} style={{ height: 32, width: 'auto', maxWidth: 160, objectFit: 'contain' }} />
            </div>
          );
        })()}
      </div>

      {/* ── Mode switcher (staff with both onboarding + CRM access; academy
          admins also get an Academy Portal option, and an agency's (e.g.
          PLYRMATCH) own admins get a Partner Academies option — picking
          either navigates away from this sidebar entirely rather than
          being tracked as a third "mode" state the way crm/onboarding
          are). The Partner Academies option deep-links straight to
          /admin/crm/partners — the page that edits the same academy
          profiles shown to players in the PLYRMATCH Portal's Academy Hub
          (see Partners.jsx's PublicProfileFields) — rather than granting
          staff any way to browse the player-facing portal itself. ── */}
      {hasBothAccess && !collapsed && (
        <div style={{ padding: '10px 12px 0' }}>
          <select
            value={isCrmMode ? 'crm' : 'onboarding'}
            onChange={(e) => {
              if (e.target.value === 'academy') navigate('/academy/home');
              else if (e.target.value === 'partners') navigate('/admin/crm/partners');
              else navigate(e.target.value === 'crm' ? '/admin/crm/dashboard' : '/admin/dashboard');
            }}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 7,
              padding: '7px 8px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <option value="crm" style={{ color: '#0f172a' }}>{t('common:sidebar.modeSwitcher.crmPreSales', 'CRM (pre-sales)')}</option>
            <option value="onboarding" style={{ color: '#0f172a' }}>{t('common:sidebar.modeSwitcher.onboardingPostSales', 'Onboarding (post-sales)')}</option>
            {isAcademyAdmin && canManageAcademyPortal && <option value="academy" style={{ color: '#0f172a' }}>{t('common:sidebar.modeSwitcher.academyPortalOption', 'Academy Portal')}</option>}
            {isAcademyAdmin && isAgencyTenant && <option value="partners" style={{ color: '#0f172a' }}>{t('common:sidebar.modeSwitcher.partnerAcademiesOption', 'Partner Academies')}</option>}
          </select>
        </div>
      )}

      {/* Coaches never toggle crm/onboarding (no CRM access), but still need
          a way to jump to the Academy Portal — their primary home. */}
      {isCoach && !collapsed && (
        <div style={{ padding: '10px 12px 0' }}>
          <button
            onClick={() => navigate('/academy/home')}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.06)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 7,
              padding: '7px 8px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {t('common:sidebar.goToAcademyPortal', 'Go to Academy Portal →')}
          </button>
        </div>
      )}

      {/* ── Nav ── */}
      <nav
        className="flex-1 overflow-y-auto"
        style={{ padding: collapsed ? '10px 6px' : '10px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {navItems.map((item) => (
          <NavItem
            key={item.to}
            {...item}
            collapsed={collapsed}
            badge={isAcademyAdmin && item.to === '/admin/messages' ? unreadCount : item.to === '/admin/crm/lead-bot' ? leadBotWaiting : 0}
          />
        ))}
      </nav>

      {/* ── User row ── */}
      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          padding: collapsed ? '12px 0' : '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <div
          style={{
            width: 28, height: 28,
            borderRadius: '50%',
            background: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {initials}
        </div>
        {!collapsed && (
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ color: '#fff', fontSize: 12, fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {userProfile?.displayName || userProfile?.email || '—'}
            </p>
            {userProfile?.displayName && (
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userProfile.email}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Collapse toggle ── */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="hidden md:flex items-center justify-center w-full"
        style={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          padding: '10px',
          color: 'rgba(255,255,255,0.35)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          transition: 'color 0.15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
        title={collapsed ? t('common:sidebar.expandSidebar', 'Expand sidebar') : t('common:sidebar.collapseSidebar', 'Collapse sidebar')}
      >
        {collapsed
          ? <ChevronRightIcon style={{ width: 14, height: 14 }} />
          : <ChevronLeftIcon  style={{ width: 14, height: 14 }} />
        }
      </button>
    </div>
  );
}
